import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiFetchEmpty, authorizedFetch } from './client';
import { keys } from './keys';
import { invalidateCommunityAudit } from './communityAudit';
import { sortByDisplayName } from '../lib/sortByName';
import { MtoQuantityMatrixSchema, MtosListSchema } from './schemas';

type MtoUploadPreserveSummary = {
  preservedLineCount?: number;
  skippedLineCount?: number;
  skipped?: Array<{ itemId?: string; payableTaskGroup?: string; reasons?: string[] }>;
};

export function useCommunityMtos(communityId: string | undefined) {
  return useQuery({
    queryKey: keys.communityMtos(communityId ?? ''),
    enabled: !!communityId,
    queryFn: async () => {
      const rows = await apiFetch(`/api/communities/${communityId}/mtos`, MtosListSchema);
      return sortByDisplayName(rows);
    },
  });
}

export function useCommunityMtoQuantities(communityId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.communityMtoQuantities(communityId ?? ''),
    enabled: !!communityId && enabled,
    queryFn: () =>
      apiFetch(`/api/communities/${communityId}/mto-quantities`, MtoQuantityMatrixSchema),
  });
}

export function useUploadMtos(communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (files: File[]) => {
      const fd = new FormData();
      for (const f of files) fd.append('files', f);
      const res = await authorizedFetch(`/api/communities/${communityId}/upload`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error || 'Upload failed');
      }
      return res.json() as Promise<{
        imported?: number;
        totalLines?: number;
        errors?: unknown[];
        preserveSummary?: MtoUploadPreserveSummary;
      }>;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.communityMtos(communityId ?? '') });
      void qc.invalidateQueries({ queryKey: keys.builders() });
      invalidateCommunityAudit(qc, communityId);
    },
  });
}

export function useUploadCommunityCsv(communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authorizedFetch(`/api/communities/${communityId}/upload-csv`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error || 'Upload failed');
      }
      return res.json() as Promise<{
        added?: unknown[];
        preserveSummary?: MtoUploadPreserveSummary;
      }>;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.communityMtos(communityId ?? '') });
      invalidateCommunityAudit(qc, communityId);
    },
  });
}

export function useDeleteMto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mtoId }: { mtoId: number; communityId: string }) =>
      apiFetchEmpty(`/api/mtos/${mtoId}`, { method: 'DELETE' }),
    onSuccess: (_, v) => {
      void qc.invalidateQueries({ queryKey: keys.communityMtos(v.communityId) });
      invalidateCommunityAudit(qc, v.communityId);
    },
  });
}
